import React from 'react';
import { Avatar, Divider, MenuItem, Select } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import AccountCircle from '@mui/icons-material/AccountCircle';

import { AppMenu, AppMenuItem, selectMenuProps } from '../common/menu/primitives';
import {
  selectSelectionMode, selectSelectedCount, enterSelectionMode,
} from '../../redux/features/context/contextSlice';
import { isPublicNamespace } from '../../common/ItemTools';
import { sourceName } from '../common/sources';
import { AvatarButton, LoginButton } from './styles';

const SOURCE_KEYS = ['cl', 'hn', 'lb', 'rd', 'gh', 'yt', 'tw'];
const SOURCED_TYPES = ['items', 'authors', 'sites', 'lists'];

// Entities whose grid draws a SelectionBar only while moderating. Items draw one
// in every non-public namespace; tags draw none at all.
const MODERATION_SELECTABLE = ['authors', 'sites', 'lists'];

const SOURCE_SELECT_SX = {
  width: '100%',
  fontSize: '1rem',
  '& .MuiSelect-select': { padding: '6px 16px' },
};

/**
 * The account control at the right of the header: a sign-in button when signed
 * out, otherwise the avatar and the menu behind it.
 *
 * The menu is where the actions that don't earn a place in the toolbar live.
 */
export default function AccountMenu({
  userInfo, context, isModerate, pageEntityIds,
  onSignIn, onSettings, onImport, onBulkAddToList, onLogout, onSourceChange,
}) {
  const dispatch = useDispatch();
  const selectionMode = useSelector(selectSelectionMode);
  const selectedCount = useSelector(selectSelectedCount);

  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const closeMenu = () => setMenuAnchor(null);

  const givenName = userInfo?.givenName;
  const picture = userInfo?.picture;
  if (!givenName) {
    return <LoginButton onClick={onSignIn}>Sign in</LoginButton>;
  }

  const isPublic = isPublicNamespace(context?.namespace);
  const isItemContext = context?.type === 'items';
  const canSelect = !isPublic
    && context?.view !== 'share'
    && (isItemContext || (isModerate && MODERATION_SELECTABLE.includes(context?.type)));
  const showSourcePicker = userInfo.isModerator && SOURCED_TYPES.includes(context?.type);

  return (
    <>
      <AvatarButton onClick={(event) => setMenuAnchor(event.currentTarget)}>
        { picture
          ? <Avatar alt={givenName} src={picture} sx={{ width: 28, height: 28 }} />
          : <AccountCircle sx={{ width: 28, height: 28 }} /> }
      </AvatarButton>
      <AppMenu
        anchorEl={menuAnchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        elevation={0}
        id="menu-appbar"
        open={Boolean(menuAnchor)}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        onClose={closeMenu}
      >
        <AppMenuItem>{givenName}</AppMenuItem>
        <Divider />
        <AppMenuItem
          key="settings"
          onClick={() => { closeMenu(); onSettings(); }}
        >
          Settings
        </AppMenuItem>
        {canSelect && (
          <AppMenuItem
            key="select"
            onClick={() => {
              closeMenu();
              if (!selectionMode) {
                dispatch(enterSelectionMode(isModerate ? pageEntityIds : undefined));
              }
            }}
          >
            {/* context.type is already the plural noun the grid holds */}
            {`${selectionMode ? 'Selecting' : 'Select'} ${context.type}`}
            {selectedCount > 0 && selectionMode && ` (${selectedCount})`}
          </AppMenuItem>
        )}
        {canSelect && isItemContext && selectionMode && selectedCount > 0 && (
          <AppMenuItem
            key="addToList"
            onClick={() => {
              closeMenu();
              onBulkAddToList();
            }}
          >
            {`Add to list (${selectedCount})`}
          </AppMenuItem>
        )}
        <AppMenuItem
          key="importUrls"
          onClick={() => { closeMenu(); onImport(); }}
        >
          Import
        </AppMenuItem>
        { showSourcePicker && <Divider /> }
        { showSourcePicker && (
          <AppMenuItem key="source" disableRipple sx={{ padding: 0 }}>
            <Select
              id="menu-source-select"
              value={context.source || 'cl'}
              onChange={(e) => { onSourceChange(e.target.value); closeMenu(); }}
              variant="standard"
              disableUnderline
              sx={SOURCE_SELECT_SX}
              MenuProps={selectMenuProps}
            >
              {SOURCE_KEYS.map((key) => (
                <MenuItem key={key} value={key}>{sourceName(key)}</MenuItem>
              ))}
            </Select>
          </AppMenuItem>
        )}
        <Divider />
        <AppMenuItem
          key="logout"
          onClick={() => { closeMenu(); onLogout(); }}
        >
          Sign out
        </AppMenuItem>
      </AppMenu>
    </>
  );
}
